import { Pointer } from '@yleisradio/areena-types';
import { Button } from 'components/Button';
import { useTranslation } from 'hooks/useTranslation';
import React, { useState } from 'react';
import { Program } from '../Program/Program';
import styles from './ChannelPrograms.module.scss';
import { AnalyticsContextProvider } from 'contexts/AnalyticsContext';
import { SummaryViewEventParams } from 'services/snowplowAnalytics/contextGenerators';
import { createSchedule } from '../utils';
import { useCards } from 'hooks/useCards';

interface ChannelProgramsProps {
  source: Pointer;
  isFutureDate: boolean;
  isToday: boolean;
  showExpired: boolean;
  currentTime: number;
  sendSummaryViewEvent: (params: SummaryViewEventParams) => void;
}

const DEFAULT_SHOWN_PROGRAMS = 15;

export const ChannelPrograms = ({
  source,
  isFutureDate,
  isToday,
  showExpired,
  currentTime,
  sendSummaryViewEvent,
}: ChannelProgramsProps) => {
  const { cards, loading, errored, meta } = useCards({
    source,
    pageIndex: 0,
    pageSize: 100,
  });
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const t = useTranslation();

  const { expired, current, upcoming } = createSchedule(
    cards,
    currentTime,
    source.uri
  );

  const programs = [
    ...(showExpired || !isToday ? expired : []),
    ...current,
    ...(showAllUpcoming || !isToday
      ? upcoming
      : upcoming.slice(0, DEFAULT_SHOWN_PROGRAMS - current.length)),
  ];

  const totalProgramCount = current.length + upcoming.length;

  const getErrorMessage = (): string => {
    if (isFutureDate) return t('noScheduleAvailableYet');

    if (isToday && programs.length > 0) return t('endOfSchedule');

    return t('noTransmissions');
  };

  return (
    <div className={styles.programsRoot}>
      {!loading ? (
        <AnalyticsContextProvider context={meta?.analytics?.context?.comscore}>
          {programs.length > 0 ? (
            <ol className={styles.programList}>
              {programs.map((prog) => (
                <li key={prog.key} className={styles.programWrapper}>
                  <Program
                    currentTime={currentTime}
                    program={prog}
                    listKey={source.uri}
                    sendSummaryViewEvent={sendSummaryViewEvent}
                  />
                </li>
              ))}
            </ol>
          ) : (
            <div className={styles.message}>{getErrorMessage()}</div>
          )}

          {isToday && totalProgramCount > DEFAULT_SHOWN_PROGRAMS && (
            <div className={styles.showMoreButtonContainer}>
              <Button
                size="xs"
                variant="secondary"
                onClick={() => setShowAllUpcoming((state) => !state)}
                text={showAllUpcoming ? t('showLessGuide') : t('showMoreGuide')}
              />
            </div>
          )}
        </AnalyticsContextProvider>
      ) : null}
      {errored && (
        <div className={styles.message}>{t('channelErrorMessage')}</div>
      )}
    </div>
  );
};
